// src/components/admin/flows/ExecutionHistory.js
import React, { useEffect, useState } from 'react';
import {
    History, CheckCircle, XCircle, Clock, AlertTriangle,
    RefreshCw, Search, ChevronDown
} from 'lucide-react';
import { useFlowAutomation } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import ExecutionDetail from './ExecutionDetail';

const ExecutionHistory = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { executions, workflows, fetchExecutions, loading } = useFlowAutomation();
    const [selectedExecutionId, setSelectedExecutionId] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [workflowFilter, setWorkflowFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const statusArg = statusFilter && statusFilter.length > 0 ? statusFilter : undefined;
        fetchExecutions(null, statusArg ? { status: statusArg } : {});
    }, [fetchExecutions, statusFilter]);

    const filteredExecutions = executions.filter(exec => {
        if (workflowFilter && exec.workflow_id !== workflowFilter) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (
                !exec.workflow_name?.toLowerCase().includes(term) &&
                !exec.triggered_by?.toLowerCase().includes(term)
            ) return false;
        }
        return true;
    });

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
            case 'running': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
            case 'cancelled': return <AlertTriangle className="w-4 h-4 text-gray-500" />;
            default: return <Clock className="w-4 h-4 text-amber-500" />;
        }
    };

    const formatDuration = (startedAt, completedAt) => {
        if (!startedAt) return '—';
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const diffMs = end - start;
        const minutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m`;
        return `${Math.floor(diffMs / 1000)}s`;
    };

    const inputClass = `px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    // Show detail view when an execution is selected
    if (selectedExecutionId) {
        return (
            <ExecutionDetail
                executionId={selectedExecutionId}
                onBack={() => setSelectedExecutionId(null)}
            />
        );
    }

    if (!loading && executions.length === 0 && !statusFilter) {
        return (
            <EmptyState
                icon={History}
                title="No executions yet"
                description="Workflow execution logs will appear here once your workflows start running."
            />
        );
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                        type="text"
                        placeholder="Search executions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`${inputClass} pl-9 w-full`}
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={inputClass}
                >
                    <option value="">All Statuses</option>
                    <option value="running">Running</option>
                    <option value="waiting_approval">Waiting Approval</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
                <select
                    value={workflowFilter}
                    onChange={(e) => setWorkflowFilter(e.target.value)}
                    className={inputClass}
                >
                    <option value="">All Workflows</option>
                    {workflows.map(wf => (
                        <option key={wf.id} value={wf.id}>{wf.name}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <table className="w-full">
                    <thead>
                        <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Status
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Workflow
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Triggered By
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Started
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Duration
                            </th>
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {filteredExecutions.length === 0 ? (
                            <tr>
                                <td colSpan={5} className={`px-4 py-8 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    No executions match your filters
                                </td>
                            </tr>
                        ) : (
                            filteredExecutions.map((exec) => (
                                <tr
                                    key={exec.id}
                                    onClick={() => setSelectedExecutionId(exec.id)}
                                    className={`${isDark ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white hover:bg-gray-50'} cursor-pointer transition-colors`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {getStatusIcon(exec.status)}
                                            <StatusBadge status={exec.status} />
                                        </div>
                                    </td>
                                    <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {exec.workflow_name || 'Unknown'}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {exec.triggered_by || 'System'}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {exec.started_at ? new Date(exec.started_at).toLocaleString() : '—'}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {formatDuration(exec.started_at, exec.completed_at)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExecutionHistory;
